import React from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Typography, Button, Container as MuiContainer,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { BRAND_SANS_FONT, brandConfig } from '../../shared/design/theme';
import {
  LandingContainer,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
} from '../home/HomePage.styles';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { DeckVisualPanel } from '../../shared/components/DeckVisuals';
import SEO from '../../shared/components/SEO';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';
import { comparisons } from './comparisonsData';

/**
 * /compare/:slug - versus pages.
 *
 * One component, slug-driven from comparisonsData.js. Each entry can carry
 * one OR more comparison blocks (the Claude page compares both the chat app
 * and the PowerPoint add-in), optional "real difference" cards, optional
 * when-to-use callouts, and an optional closing line.
 *
 * Design: shares the landing-page system - SectionEyebrow / SectionTitle /
 * SectionSubtitle typography, a single 20px panel radius (BaseCard), the rust
 * accent (#B83B1D), and the landing closing-CTA pattern. The page opens
 * straight into the comparison (no top CTA); the CTA lives once, at the end.
 */

const ACCENT = '#B83B1D';
const PANEL_RADIUS = '20px';
const BTN_RADIUS = '14px';

// Shared button states. Focus-visible and active were missing on both CTAs.
const btnStates = {
  transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
  '&:active': { transform: 'translateY(1px)' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

// Hover lift, suppressed for readers who asked for less motion.
const CARD_HOVER = {
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    borderColor: alpha(ACCENT, 0.28),
    boxShadow: `0 10px 28px ${alpha('#2E140A', 0.07)}`,
    transform: 'translateY(-2px)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
  },
};

// One rhythm for every band on the page.
const SECTION_MB = { xs: 6, md: 8 };
// Reading measure for running prose. At the body size below this lands around
// 70 characters a line; the previous 740/780 caps ran to roughly 90.
const PROSE_MAX = 640;

// Proof line under the hero. The model line names the families the in-product
// picker actually offers (src/screens/chat/ai-chat/constants.js), which is why
// it names MODELS rather than vendors: Gemini is reached through OpenRouter,
// so a partnership claim would be false where a model-family claim is not.
// The training-data line is the security answer in the shared FAQ. None of it
// is about the competitor.
const TRUST_SIGNALS = [
  'Claude, GPT, or Gemini, your pick',
  'Works inside your live PowerPoint, Excel, and Word',
  'Never trained on your data',
];

// Risk reversal. Same wording as the support FAQ (faqChatService.js, "refund")
// and the pricing page.
const GUARANTEE = '30-day money-back guarantee. Billed monthly, cancel anytime.';

// Product questions every comparison page inherits, pulled from the one
// curated repository so a BOFU reader gets the procurement answers without a
// second tab, and so these can never drift from the support widget.
const SHARED_FAQ_IDS = ['pricing', 'powerpoint-versions', 'security', 'refund'];
const SHARED_FAQ = SHARED_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean)
  .map(({ question, answer }) => ({ q: question, a: answer }));

const splitParagraphs = (text) =>
  String(text || '')
    .split('\n\n')
    .map((s) => s.trim())
    .filter(Boolean);

export default function ComparePage() {
  const { slug } = useParams();
  const theme = useTheme();
  // Resolve from either URL form: the short share URL /vs/claude (slug =
  // "claude") or the legacy /compare/restack-vs-claude (slug = full key).
  const data = comparisons[slug] || comparisons[`restack-vs-${slug}`];

  if (!data) return <Navigate to="/" replace />;

  const ink = theme.palette.text.primary;
  // Canonical is the short, shareable /vs/<competitor> URL.
  const shortSlug = data.slug.replace(/^restack-vs-/, '');
  const canonicalPath = `/vs/${shortSlug}`;
  const url = `https://re-stack.com${canonicalPath}`;

  // Competitor-scoped questions first (they are why the reader is here), then
  // the shared product questions.
  const faq = [...(data.faq || []), ...SHARED_FAQ];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: data.metaTitle,
      description: data.metaDescription,
      author: { '@type': 'Organization', name: 'Restack', url: 'https://re-stack.com/' },
      publisher: {
        '@type': 'Organization',
        name: 'Restack',
        logo: { '@type': 'ImageObject', url: 'https://re-stack.com/logo.svg' },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      about: [
        { '@type': 'SoftwareApplication', name: 'Restack', url: 'https://re-stack.com/' },
        { '@type': 'SoftwareApplication', name: data.competitorName },
      ],
    },
    // Mirrors the rendered accordion exactly, so the rich result and the page
    // can never disagree.
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ];

  // ── Shared renderers ─────────────────────────────────────────────────
  const paragraphs = (text, key, { center = false } = {}) => {
    const parts = splitParagraphs(text);
    return parts.map((p, i) => (
      <Typography
        key={`${key}-${i}`}
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: { xs: '1rem', md: '1.08rem' },
          lineHeight: 1.7,
          color: alpha(ink, 0.82),
          textAlign: center ? 'center' : 'left',
          mb: i === parts.length - 1 ? 0 : 2,
        }}
      >
        {p}
      </Typography>
    ));
  };

  const sectionHeadingSx = {
    fontFamily: BRAND_SANS_FONT,
    fontWeight: 700,
    fontSize: { xs: '1.4rem', md: '1.7rem' },
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: ink,
    mb: 2.25,
  };

  const ComparisonTable = ({ competitorLabel, rows }) => {
    const grid = { display: 'grid', gridTemplateColumns: { xs: '1.1fr 1fr 1fr', md: '1.4fr 1fr 1fr' } };
    const cellBase = { p: { xs: 1.5, md: 2 }, fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.8rem', md: '0.9rem' }, lineHeight: 1.5 };
    // The middle (Restack) column is a continuous tinted "lane" so the eye
    // lands on Restack as the protagonist.
    const lane = { backgroundColor: alpha(ACCENT, 0.05), borderLeft: `1px solid ${alpha(ACCENT, 0.12)}`, borderRight: `1px solid ${alpha(ACCENT, 0.12)}` };

    return (
      <Box sx={{ border: `1px solid ${alpha(ink, 0.1)}`, borderRadius: PANEL_RADIUS, overflow: 'hidden', backgroundColor: '#ffffff', boxShadow: `0 1px 2px ${alpha('#2E140A', 0.04)}` }}>
        {/* Header */}
        <Box sx={{ ...grid, borderBottom: `1px solid ${alpha(ink, 0.08)}` }}>
          <Box sx={{ ...cellBase, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: alpha(ink, 0.5), backgroundColor: alpha(ink, 0.03), display: 'flex', alignItems: 'center' }}>
            Feature
          </Box>
          <Box sx={{ ...cellBase, ...lane, backgroundColor: alpha(ACCENT, 0.09), display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <brandConfig.icon sx={{ fontSize: 15, color: ACCENT }} />
            <Box component="span" sx={{ fontWeight: 700, fontSize: '0.85rem', color: ACCENT }}>Restack</Box>
          </Box>
          <Box sx={{ ...cellBase, fontWeight: 700, fontSize: '0.85rem', color: alpha(ink, 0.65), backgroundColor: alpha(ink, 0.03), display: 'flex', alignItems: 'center' }}>
            {competitorLabel}
          </Box>
        </Box>
        {/* Rows */}
        {rows.map((row, idx) => {
          const last = idx === rows.length - 1;
          const rowBorder = last ? 'none' : `1px solid ${alpha(ink, 0.06)}`;
          return (
            <Box key={row.feature} sx={grid}>
              <Box sx={{ ...cellBase, fontWeight: 600, color: alpha(ink, 0.72), borderBottom: rowBorder }}>
                {row.feature}
              </Box>
              <Box sx={{ ...cellBase, ...lane, color: ink, fontWeight: 500, borderBottom: rowBorder }}>
                {row.restack}
              </Box>
              <Box sx={{ ...cellBase, color: alpha(ink, 0.58), borderBottom: rowBorder }}>
                {row.competitor}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // One side of a head-to-head contrast tile. The Restack side carries the rust
  // lane treatment from the table so the eye still lands on Restack.
  const ContrastBlock = ({ label, value, restack }) => (
    <Box
      sx={{
        borderRadius: '11px',
        p: { xs: 1.25, md: 1.4 },
        backgroundColor: restack ? alpha(ACCENT, 0.06) : alpha(ink, 0.02),
        border: `1px solid ${restack ? alpha(ACCENT, 0.16) : alpha(ink, 0.06)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.7 }}>
        {restack ? (
          <brandConfig.icon sx={{ fontSize: 13, color: ACCENT }} />
        ) : (
          <Box sx={{ width: 11, height: 2, borderRadius: 2, backgroundColor: alpha(ink, 0.3) }} />
        )}
        <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: restack ? ACCENT : alpha(ink, 0.5) }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.85rem', lineHeight: 1.45, fontWeight: restack ? 600 : 400, color: restack ? ink : alpha(ink, 0.6) }}>
        {value}
      </Typography>
    </Box>
  );

  // Bento version of a comparison block, text-led like /use-cases: a full-width
  // lead tile where the verdict carries the point and the proof visual sits below
  // as a quiet supporting strip, followed by one head-to-head tile per feature
  // row. Used in place of the table when a comparison block carries a `visual`.
  const ComparisonBento = ({ c }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr)' }, gap: { xs: 2, md: 2.5 }, alignItems: 'stretch' }}>
      <Box
        sx={{
          gridColumn: { xs: 'auto', md: 'span 6' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: PANEL_RADIUS,
          border: `1px solid ${alpha(ink, 0.08)}`,
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {c.verdict && (
          <Box sx={{ p: { xs: 2.5, md: 3 }, pb: { xs: 1.5, md: 2 } }}>
            <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '1.05rem', md: '1.2rem' }, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4, color: ink }}>
              {c.verdict}
            </Typography>
          </Box>
        )}
        <DeckVisualPanel
          visual={c.visual}
          variant="support"
          sx={{ mt: 'auto', mx: { xs: 2, md: 2.5 }, mb: { xs: 2, md: 2.5 }, maxWidth: 560, alignSelf: 'stretch', width: 'auto' }}
        />
      </Box>

      {c.rows.map((row) => (
        <Box
          key={row.feature}
          sx={{
            gridColumn: { xs: 'auto', md: 'span 3' },
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2.25, md: 2.5 },
            borderRadius: PANEL_RADIUS,
            border: `1px solid ${alpha(ink, 0.08)}`,
            backgroundColor: '#ffffff',
            ...CARD_HOVER,
          }}
        >
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: alpha(ink, 0.45), mb: 1.25 }}>
            {row.feature}
          </Typography>
          <ContrastBlock restack label="Restack" value={row.restack} />
          <Box sx={{ mt: 1 }}>
            <ContrastBlock label={c.competitorLabel} value={row.competitor} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  const diffItems = data.differentiators?.items || [];
  const diffCols = diffItems.length >= 3 ? 'repeat(3, 1fr)' : `repeat(${Math.max(diffItems.length, 1)}, 1fr)`;
  const hasWhen = data.whenCompetitor || data.whenRestack;

  return (
    <LandingContainer data-theme="light" sx={{ position: 'relative' }}>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        canonical={canonicalPath}
        ogType="article"
        schema={schema}
      />

      <SiteHeader />

      <MuiContainer
        component="main"
        id="main-content"
        sx={{ position: 'relative', zIndex: 1, maxWidth: 920, pt: { xs: 'calc(56px + 2.5rem)', md: 'calc(64px + 3.5rem)' }, pb: { xs: 7, md: 11 } }}
      >
        {/* Hero - shared section typography */}
        <CenteredHeader sx={{ mb: { xs: 4, md: 5 } }}>
          <SectionEyebrow>{data.hero.eyebrow}</SectionEyebrow>
          <SectionTitle component="h1">
            Restack vs <em>{data.competitorName}</em>
          </SectionTitle>
          <SectionSubtitle sx={{ maxWidth: 720 }}>{data.hero.subhead}</SectionSubtitle>

          {/* Proof line. A verification-intent reader arrives sceptical and
              previously met nothing but assertion until the footer. */}
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 3,
              p: 0,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              columnGap: 1.75,
              rowGap: 0.75,
            }}
          >
            {TRUST_SIGNALS.map((signal, i) => (
              <Box
                key={signal}
                component="li"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.8rem',
                  lineHeight: 1.4,
                  color: alpha(ink, 0.55),
                }}
              >
                {i > 0 && (
                  <Box
                    aria-hidden="true"
                    sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: alpha(ink, 0.25) }}
                  />
                )}
                {signal}
              </Box>
            ))}
          </Box>
        </CenteredHeader>

        {/* Intro lead */}
        {data.intro && (
          <Box sx={{ maxWidth: PROSE_MAX, mx: 'auto', mb: SECTION_MB, textAlign: 'center' }}>
            {paragraphs(data.intro, 'intro', { center: true })}
          </Box>
        )}

        {/* Comparison block(s). When a block carries a `visual`, the heading +
            verdict + body sit beside a bento-style proof visual (alternating
            sides), then the table runs full-width below. */}
        {data.comparisons.map((c, i) => {
          const heading = c.heading && (
            <Typography component="h2" sx={sectionHeadingSx}>{c.heading}</Typography>
          );
          const body = c.body && <Box>{paragraphs(c.body, `body-${i}`)}</Box>;

          // A comparison that carries a `visual` renders as a bento (vs/claude).
          // Everything else keeps the head-to-head table.
          if (c.visual) {
            return (
              <Box key={c.competitorLabel || i} sx={{ mb: SECTION_MB }}>
                {heading}
                {body && <Box sx={{ mb: { xs: 3, md: 3.5 }, maxWidth: PROSE_MAX }}>{body}</Box>}
                <ComparisonBento c={c} />
              </Box>
            );
          }

          const verdict = c.verdict && (
            <Box sx={{ borderLeft: `3px solid ${ACCENT}`, pl: 2.5, py: 0.25 }}>
              <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '1.05rem', md: '1.15rem' }, fontWeight: 600, lineHeight: 1.5, color: ink }}>
                {c.verdict}
              </Typography>
            </Box>
          );
          return (
            <Box key={c.competitorLabel || i} sx={{ mb: SECTION_MB }}>
              {heading}
              {verdict && <Box sx={{ mb: c.body ? 2.5 : 3, maxWidth: PROSE_MAX }}>{verdict}</Box>}
              {body && <Box sx={{ mb: 3, maxWidth: PROSE_MAX }}>{body}</Box>}
              <ComparisonTable competitorLabel={c.competitorLabel} rows={c.rows} />
            </Box>
          );
        })}

        {/* Differentiators - bento tiles with a proof visual on top, matching
            the /use-cases treatment. Items carrying a `visual` key render the
            shared deck visual; without one, the tile is text only. */}
        {diffItems.length > 0 && (
          <Box sx={{ mb: SECTION_MB }}>
            <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3 }}>
              {data.differentiators.heading}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: diffCols }, gap: { xs: 2, md: 2.5 }, alignItems: 'stretch' }}>
              {diffItems.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: PANEL_RADIUS,
                    border: `1px solid ${alpha(ink, 0.08)}`,
                    backgroundColor: '#ffffff',
                    overflow: 'hidden',
                    ...CARD_HOVER,
                  }}
                >
                  {item.visual && <DeckVisualPanel visual={item.visual} variant="top" />}
                  <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontWeight: 700, fontSize: '1.02rem', mb: 1, color: ink, letterSpacing: '-0.015em' }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.92rem', lineHeight: 1.6, color: alpha(ink, 0.7) }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* When-to-use. The two tile labels are the real headings of this
            section, so they are h3s under a proper h2 rather than styled
            paragraphs that only look like headings. */}
        {hasWhen && (
          <Box sx={{ mb: SECTION_MB }}>
            <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3 }}>
              When to use which
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 2.5 } }}>
              {data.whenCompetitor && (
                <Box sx={{ p: { xs: 2.5, md: 3 }, borderRadius: PANEL_RADIUS, border: `1px solid ${alpha(ink, 0.1)}`, backgroundColor: alpha(ink, 0.02) }}>
                  <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: alpha(ink, 0.5), mb: 1 }}>
                    {`When ${data.competitorName} is the right tool`}
                  </Typography>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.95rem', lineHeight: 1.6, color: alpha(ink, 0.8) }}>
                    {data.whenCompetitor}
                  </Typography>
                </Box>
              )}
              {data.whenRestack && (
                <Box sx={{ p: { xs: 2.5, md: 3 }, borderRadius: PANEL_RADIUS, border: `1px solid ${alpha(ACCENT, 0.25)}`, backgroundColor: alpha(ACCENT, 0.04) }}>
                  <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: ACCENT, mb: 1 }}>
                    When Restack
                  </Typography>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.95rem', lineHeight: 1.6, color: ink }}>
                    {data.whenRestack}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* FAQ. A comparison page is a bottom-of-funnel verification stop, and
            the procurement questions were the one thing it never answered. */}
        {faq.length > 0 && (
          <Box sx={{ mb: SECTION_MB }}>
            <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 2 }}>
              Questions we get
            </Typography>
            {faq.map(({ q, a }) => (
              <Accordion
                key={q}
                disableGutters
                elevation={0}
                square
                sx={{
                  backgroundColor: 'transparent',
                  borderBottom: `1px solid ${alpha(ink, 0.08)}`,
                  '&::before': { display: 'none' },
                }}
              >
                {/* component="span" is deliberate: MUI v7 already wraps the
                    summary in its own heading element. */}
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded sx={{ color: alpha(ink, 0.45) }} />}
                  sx={{
                    px: 0.5,
                    minHeight: 56,
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease',
                    '& .MuiAccordionSummary-content': { my: 1.25 },
                    '&:hover': { backgroundColor: alpha(ink, 0.02) },
                    '&:hover .faq-question': { color: ACCENT },
                    '&:focus-visible': {
                      outline: `2px solid ${ACCENT}`,
                      outlineOffset: '-2px',
                      backgroundColor: 'transparent',
                    },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  <Typography
                    component="span"
                    className="faq-question"
                    sx={{
                      fontFamily: BRAND_SANS_FONT,
                      fontSize: '0.98rem',
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      color: ink,
                      transition: 'color 0.2s ease',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  >
                    {q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2.5 }}>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.92rem', lineHeight: 1.7, color: alpha(ink, 0.72), maxWidth: PROSE_MAX }}>
                    {a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Closing line (when present) + CTA - landing closing-CTA pattern.
            The line under the buttons is now the risk reversal: the proof
            facts that used to sit here moved up to the hero, where a sceptical
            reader actually needs them. */}
        <Box sx={{ mt: { xs: 7, md: 9 }, pt: { xs: 5, md: 6 }, borderTop: `1px solid ${alpha(ink, 0.08)}`, textAlign: 'center' }}>
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            {data.close ? (
              <Typography component="h2" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 600, lineHeight: 1.45, letterSpacing: '-0.02em', color: ink, mb: 1.75 }}>
                {data.close}
              </Typography>
            ) : (
              <Typography component="h2" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '1.6rem', md: '2.1rem' }, fontWeight: 600, lineHeight: 1.12, letterSpacing: '-0.03em', color: ink, mb: 1.5 }}>
                See it on your <Box component="em" sx={{ fontStyle: 'normal', color: ACCENT }}>own deck.</Box>
              </Typography>
            )}
            <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.95rem', md: '1.02rem' }, lineHeight: 1.6, color: alpha(ink, 0.66), mb: 3.25 }}>
              Book a 15-minute walkthrough. We will take a real slide from your workflow and rebuild it live, inside PowerPoint.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            <Button
              component={RouterLink}
              to="/demo"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1.05rem' }} />}
              sx={{
                textTransform: 'none', fontFamily: BRAND_SANS_FONT, fontWeight: 500, fontSize: '0.95rem',
                borderRadius: BTN_RADIUS, px: 3.25, py: 1.15,
                backgroundColor: '#2E140A', color: '#FAF9F7',
                boxShadow: '0 4px 14px rgba(46, 20, 10, 0.18)',
                ...btnStates,
                '&:hover': { backgroundColor: '#1A0A04', boxShadow: '0 6px 18px rgba(46, 20, 10, 0.28)' },
              }}
            >
              Book a 15-min look
            </Button>
            <Button
              component="a"
              href="/#capability-showcase"
              sx={{
                textTransform: 'none', fontFamily: BRAND_SANS_FONT, fontWeight: 500, fontSize: '0.95rem',
                borderRadius: BTN_RADIUS, px: 3.25, py: 1.15,
                color: ink, border: `1px solid ${alpha(ink, 0.18)}`,
                ...btnStates,
                '&:hover': { borderColor: alpha(ink, 0.32), backgroundColor: alpha(ink, 0.04) },
              }}
            >
              Watch the 60-second demo
            </Button>
          </Box>
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.8rem', color: alpha(ink, 0.5), mt: 2.5 }}>
            {GUARANTEE}
          </Typography>
        </Box>
      </MuiContainer>

      <Footer />
    </LandingContainer>
  );
}
